/**
 * Structured data (JSON-LD) and document head for the public places list route.
 * Breadcrumb labels in JSON-LD use English (common practice); UI meta strings use i18n.
 */

import { env } from '@web/src/lib/env.client'
import i18n from '@web/src/lib/intl/i18n'
import { robotsMetaForEnvironment, seo } from '@web/src/lib/seo'
import { absoluteUrlForPath, baseUrl } from '@web/src/lib/utilsClient'

/** Matches `validateSearch` default — filters and JSON-LD treat this as “no radius filter”. */
export const DEFAULT_PLACE_LIST_DISTANCE = 10_000

const LIST_PATH = '/place/list'

export type PlaceListSeoItem = {
  name: string
  path: string
  imageUrl?: string | null
}

export type PlaceListSeoLoaderData = {
  seoList: {
    items: PlaceListSeoItem[]
    totalCount: number
  }
}

type PlaceListSearchForSeo = {
  name?: string
  tags?: string[]
  categories?: string[]
  location?: string
  distance?: number
  sortBy?: string
  myPlaces?: boolean
  needsReview?: boolean
}

/** True when URL search filters narrow the catalog (not “my places” / review queues). */
function hasActivePlaceListFilters(search: PlaceListSearchForSeo): boolean {
  if (search.name?.trim()) return true
  if (search.tags?.length) return true
  if (search.categories?.length) return true
  if (search.location?.trim()) return true
  if (typeof search.distance === 'number' && search.distance !== DEFAULT_PLACE_LIST_DISTANCE) return true
  return Boolean(search.sortBy)
}

function buildPlaceListBreadcrumbJsonLd(): Record<string, unknown> {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: absoluteUrlForPath('/') },
      { '@type': 'ListItem', position: 2, name: 'Place Categories', item: absoluteUrlForPath('/place') },
      { '@type': 'ListItem', position: 3, name: 'Discover Places', item: absoluteUrlForPath(LIST_PATH) },
    ],
  }
}

function buildPlaceListItemListJsonLd(input: {
  hasFilters: boolean
  items: PlaceListSeoItem[]
  totalCount: number
}): Record<string, unknown> {
  const tr = i18n.t.bind(i18n)
  const base: Record<string, unknown> = {
    '@type': 'ItemList',
    numberOfItems: input.totalCount,
  }

  if (input.hasFilters) {
    base.name = tr('Filtered place search results')
  }

  if (input.items.length > 0) {
    base.itemListElement = input.items.map((row, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: row.name,
      item: absoluteUrlForPath(row.path),
      ...(row.imageUrl ? { image: row.imageUrl } : {}),
    }))
  }

  return base
}

export function placeListPageHeadFromLoaderData(
  loaderData: PlaceListSeoLoaderData | null | undefined,
  search: PlaceListSearchForSeo
) {
  const tr = i18n.t.bind(i18n)
  const placesDescription = tr(
    'Browse swinger and lifestyle clubs and venues on our platform. Filter by distance, tags, and category to find resorts and friendly spots for meetups.'
  )
  const canonicalUrl = `${baseUrl()}${LIST_PATH}`
  const hasFilters = hasActivePlaceListFilters(search)

  const items = loaderData?.seoList.items ?? []
  const totalCount = loaderData?.seoList.totalCount ?? 0

  const searchResultsPage: Record<string, unknown> = {
    '@context': 'https://schema.org',
    '@type': 'SearchResultsPage',
    url: canonicalUrl,
    name: tr('Places'),
    description: placesDescription,
    potentialAction: {
      '@type': 'SearchAction',
      target: {
        '@type': 'EntryPoint',
        urlTemplate: `${baseUrl()}${LIST_PATH}?name={query}`,
      },
      queryInput: 'required name=query',
    },
    mainEntity: buildPlaceListItemListJsonLd({ hasFilters, items, totalCount }),
  }

  const jsonLd: Record<string, unknown>[] = [searchResultsPage, buildPlaceListBreadcrumbJsonLd()]

  const ogImage = items[0]?.imageUrl

  return seo({
    title: `${tr('Discover Places')} | ${env.VITE_APP_NAME}`,
    description: placesDescription,
    url: canonicalUrl,
    ...(ogImage ? { image: ogImage } : {}),
    robots: robotsMetaForEnvironment({
      production: 'index, follow',
      staging: 'noindex,nofollow',
    }),
    jsonLd,
  })
}
